import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiExternalLink, FiPlus, FiGlobe, FiUsers, FiEdit2, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { toast } from '../../Toast';
import RemoveItemModal from '../RemoveItemModal';
import { BackLink, HubHeader, HubNote, HubTip, hubPath, useHubSite } from '../shared';

// the Category select on Add person; Directors are the Board group, the other two the Management group
export type Category = 'Directors' | 'Management' | 'Company Secretary';
export const CATEGORIES: Category[] = ['Directors', 'Management', 'Company Secretary'];
const TAG: Record<Category, string> = { Directors: 'Director', Management: 'Management', 'Company Secretary': 'Company Secretary' };
export type Group = 'board' | 'management';
export const groupOf = (c: Category): Group => c === 'Directors' ? 'board' : 'management';

export interface Person {
    id: string;
    name: string;
    role: string;
    category: Category;
    visible: boolean;
    // the website page is being rewritten after a save
    updating?: boolean;
    qualifications?: string;
    bio?: string;
    linkedin?: string;
    email?: string;
}

// sample people (spec 405:9654), in website order; shared with AddPerson.tsx for the edit prefill
export const PEOPLE: Person[] = [
    { id: 'pw', name: 'Peter Williams', role: 'Non-Executive Chairman', category: 'Directors', visible: true },
    { id: 'wt', name: 'Wallace Turner', role: 'Managing Director & CEO', category: 'Directors', visible: true },
    {
        id: 'sn', name: 'Dr Sarah Nguyen', role: 'Non-Executive Director', category: 'Directors', visible: true, qualifications: 'BSc (Geology), PhD, MAusIMM',
        bio: 'Dr Nguyen is a geologist with more than 25 years’ experience in gold and base-metal exploration across Nevada and Western Australia. She was previously Chief Geologist at a major ASX gold producer and holds a PhD from the University of Western Australia.\n\nShe joined the 49 Metals board in March 2025 and chairs the Audit & Risk Committee.',
        linkedin: 'linkedin.com/in/sarah-nguyen-geo',
    },
    { id: 'jh', name: 'James Hartley', role: 'Non-Executive Director', category: 'Directors', visible: true },
    { id: 'rf', name: 'Rob Fletcher', role: 'Chief Financial Officer & Company Secretary', category: 'Company Secretary', visible: true },
    { id: 'mc', name: 'Megan Cole', role: 'Exploration Manager — Nevada', category: 'Management', visible: true },
    { id: 'tb', name: 'Tom Blanchi', role: 'Investor Relations Manager', category: 'Management', visible: false, updating: true },
];

export const initialsOf = (name: string) => name.replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

// Feather has no grip; six dots on the same 24-grid
export const GripIcon: React.FC = () =>
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        {[9, 15].map(x => [5, 12, 19].map(y => <circle key={`${x}-${y}`} cx={x} cy={y} r={1.6} />))}
    </svg>;

const GROUPS: { group: Group; title: string; sub: string }[] = [
    { group: 'board', title: 'Board of directors', sub: 'Shown first on the page' },
    { group: 'management', title: 'Management team', sub: 'Shown below the board' },
];

// Website Hub › Website content › Board & management (spec 405:9654)
const BoardManagement: React.FC = () => {
    const history = useHistory();
    const { host, short, url } = useHubSite();
    const [people, setPeople] = useState(PEOPLE);
    const [dragging, setDragging] = useState<string | null>(null);
    const [removing, setRemoving] = useState<Person | null>(null);
    const moved = useRef(false);

    const edit = (p: Person) => history.push(`${hubPath('content', 'board-management', 'edit')}?id=${p.id}`);
    const toggle = (p: Person) => {
        setPeople(xs => xs.map(x => x.id === p.id ? { ...x, visible: !x.visible } : x));
        toast.info(`Preview only: ${p.name} ${p.visible ? 'hidden' : 'shown'} here, the website wasn’t changed`);
    };
    // live reorder while dragging over another card in the same group
    const dragOver = (e: React.DragEvent, over: Person) => {
        e.preventDefault();
        if (!dragging || dragging === over.id) return;
        setPeople(xs => {
            const from = xs.findIndex(x => x.id === dragging);
            if (from < 0 || groupOf(xs[from].category) !== groupOf(over.category)) return xs;
            const next = xs.filter(x => x.id !== dragging);
            moved.current = true;
            next.splice(next.findIndex(x => x.id === over.id) + (from > xs.findIndex(x => x.id === over.id) ? 0 : 1), 0, xs[from]);
            return next;
        });
    };
    const dragEnd = () => {
        setDragging(null);
        if (!moved.current) return;
        moved.current = false;
        toast.info('Preview only: order changed here, the website wasn’t changed');
    };

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="Board & Management" sub={`People shown on ${host}/about-us/board-management/ — drag cards to change the order on the website`}>
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/board-management')}><FiCode /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url('/about-us/board-management/')} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>
            <button type="button" className="btn-primary with-icon" onClick={() => history.push(hubPath('content', 'board-management', 'add'))}><FiPlus /> Add Person</button>
        </HubHeader>

        <HubNote icon={<FiGlobe />} title="Each person gets their own page on the website">
            Publishing creates a page at {host}/about-us/board-management/name/ with the biography on it. These are a separate post type — they never mix with news and media.
        </HubNote>

        {GROUPS.map(g => {
            const list = people.filter(p => groupOf(p.category) === g.group);
            return <section key={g.group} className="card hub-people">
                <div className="section-title">
                    <span className="icon-tile"><FiUsers /></span>
                    <div className="text"><h2>{g.title}</h2><span className="hint">{g.sub}</span></div>
                    <span className="hub-pill grey">{list.length} {list.length === 1 ? 'person' : 'people'}</span>
                </div>
                <div className="hub-people-grid">
                    {list.map(p => <div key={p.id} className={`hub-person${dragging === p.id ? ' dragging' : ''}`} draggable
                        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragging(p.id); }}
                        onDragOver={e => dragOver(e, p)} onDragEnd={dragEnd}>
                        <span className="hub-grip" title="Drag to reorder"><GripIcon /></span>
                        <span className="hub-person-photo">{initialsOf(p.name)}</span>
                        <strong>{p.name.replace('49 Metals', short)}</strong>
                        <span className="hub-person-role">{p.role}</span>
                        <span className="hub-person-tags">
                            <span className="hub-pill outline">{TAG[p.category]}</span>
                            <button type="button" className={`hub-pill ${p.visible ? 'green' : 'amber'}`} title={p.visible ? 'Hide from the website' : 'Show on the website'} onClick={() => toggle(p)}>
                                {p.visible ? 'On website' : 'Hidden'}
                            </button>
                            {p.updating && <span className="hub-pill amber hub-updating"><FiRefreshCw /> Updating…</span>}
                        </span>
                        <span className="hub-person-actions">
                            <button type="button" className="btn-outline with-icon" onClick={() => edit(p)}><FiEdit2 /> Edit</button>
                            <button type="button" className="hub-icon-btn danger" aria-label={`Remove ${p.name}`} title="Remove" onClick={() => setRemoving(p)}><FiTrash2 /></button>
                        </span>
                    </div>)}
                </div>
            </section>;
        })}

        <HubTip>Each person has a photo, a short biography and optional LinkedIn link. Investors can click a name on the website to read the full biography.</HubTip>

        {removing && <RemoveItemModal noun="Person" name={removing.name} onClose={() => setRemoving(null)}
            onHide={() => { setPeople(xs => xs.map(x => x.id === removing.id ? { ...x, visible: false } : x)); setRemoving(null); toast.info('Preview only: hidden here, the website wasn’t changed'); }}
            onRemove={() => { setPeople(xs => xs.filter(x => x.id !== removing.id)); setRemoving(null); toast.info('Preview only: removed here, the website wasn’t changed'); }} />}
    </>;
};

export default BoardManagement;
